/**
 * Inline SVG icon set for the Game Pass live preview.
 *
 * These mirror the small glyphs the in-app Game Pass screen renders (back,
 * info, clock, up/down stat arrows, tick, gift). They use `currentColor` so the
 * preview's CSS can theme them from the editable palette variables.
 */

import { type FC, type SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

const base = (props: IconProps) => ({
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  "aria-hidden": true,
  focusable: false,
  ...props,
});

export const BackIcon: FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path
      d="M15 18l-6-6 6-6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const InfoIcon: FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M12 11v5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <circle cx="12" cy="8" r="1" fill="currentColor" />
  </svg>
);

export const ClockIcon: FC<IconProps> = (props) => (
  <svg {...base({ width: 16, height: 16, viewBox: "0 0 16 16", ...props })}>
    <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.4" />
    <path
      d="M8 4.75V8l2.25 1.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ArrowUpIcon: FC<IconProps> = (props) => (
  <svg {...base({ width: 8, height: 8, viewBox: "0 0 8 8", ...props })}>
    <path
      d="M4 1.5L7 6H1L4 1.5z"
      fill="currentColor"
    />
  </svg>
);

export const ArrowDownIcon: FC<IconProps> = (props) => (
  <svg {...base({ width: 8, height: 8, viewBox: "0 0 8 8", ...props })}>
    <path d="M4 6.5L1 2h6L4 6.5z" fill="currentColor" />
  </svg>
);

export const TickIcon: FC<IconProps> = (props) => (
  <svg {...base({ width: 16, height: 12, viewBox: "0 0 16 12", ...props })}>
    <path
      d="M1.5 6.5L5.5 10.5L14.5 1.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const GiftIcon: FC<IconProps> = (props) => (
  <svg {...base(props)}>
    <path
      d="M20 8H4v3h16V8zM12 8v12M5 11v8a1 1 0 001 1h12a1 1 0 001-1v-8M12 8S10.5 4 8.5 4 6 6 7.5 7.2C8.7 8 12 8 12 8zM12 8s1.5-4 3.5-4S18 6 16.5 7.2C15.3 8 12 8 12 8z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
